import React, { FC, useState } from 'react';
import {
    Alert,
    Box, Button, Chip, CircularProgress, Typography,
} from '@mui/material';
import { ScanResultExportDataTypes, ScanResultExportFormats } from '@services';
import ModalDialogComponent from '../ModalDialogComponent/ModalDialogComponent';
import ScanExportConfigComponent from '../ScanExportConfigComponent/ScanExportConfigComponent';

export interface ScanExportFormData {
    awsAccountId: string;
    dataFormat: ScanResultExportFormats;
    dataTypes: ScanResultExportDataTypes;
}

interface CurrentConfig {
    status?: string | null;
    merchantAwsAccountId?: string | null;
    assumeRoleArn?: string | null;
    assumeRoleExternalId?: string | null;
    dataFormat?: string | null;
    dataTypes?: string | null;
}

interface ScanExportPanelComponentProps {
    title: string;
    onConfigure: (data: ScanExportFormData) => Promise<void>;
    onDisable: () => Promise<void>;
    isConfiguring?: boolean;
    isDisabling?: boolean;
    currentConfig?: CurrentConfig | null;
}

const defaultFormData: ScanExportFormData = {
    awsAccountId: '',
    dataFormat: 'Csv',
    dataTypes: 'MeasurementsAndSizeMatch',
};

const ScanExportPanelComponent: FC<ScanExportPanelComponentProps> = ({
                                                                         title,
                                                                         onConfigure,
                                                                         onDisable,
                                                                         isConfiguring,
                                                                         isDisabling,
                                                                         currentConfig,
                                                                     }) => {
    const isActive = currentConfig != null;
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [formData, setFormData] = useState<ScanExportFormData>(defaultFormData);
    const [isFormValid, setIsFormValid] = useState(false);

    const handleFormChange = (data: ScanExportFormData, hasErrors: boolean) => {
        setFormData(data);
        setIsFormValid(!hasErrors);
    };

    const handleConfigure = () => {
        onConfigure(formData).then(() => {
            setShowConfigModal(false);
            setFormData(defaultFormData);
            setIsFormValid(false);
        });
    };

    return (
        <Box data-testid="ScanExportPanelComponent">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h4">{title}</Typography>
                        <Chip
                            size="small"
                            color={isActive ? 'success' : 'default'}
                            label={isActive ? 'Active' : 'Inactive'}
                        />
                    </Box>
                    <Alert
                        severity="info"
                    >
                        Enabling export on scanner profile level overrides merchant level export.
                    </Alert>

                    {isActive && currentConfig && (
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                            <Typography variant="body2">
                                <strong>AWS Account ID:</strong>
                                {' '}
                                {currentConfig.merchantAwsAccountId}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Role ARN:</strong>
                                {' '}
                                {currentConfig.assumeRoleArn}
                            </Typography>
                            {currentConfig.assumeRoleExternalId && (
                                <Typography variant="body2">
                                    <strong>External ID:</strong>
                                    {' '}
                                    {currentConfig.assumeRoleExternalId}
                                </Typography>
                            )}
                            <Typography variant="body2">
                                <strong>Data Format:</strong>
                                {' '}
                                {currentConfig.dataFormat}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Data Types:</strong>
                                {' '}
                                {currentConfig.dataTypes}
                            </Typography>
                        </Box>
                    )}
                    <Box>
                        {isActive ? (
                            <Button
                                variant="contained"
                                color="info"
                                onClick={onDisable}
                                disabled={isDisabling}
                                startIcon={isDisabling ? <CircularProgress size={16} /> : undefined}
                            >
                                Disable Export
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => setShowConfigModal(true)}
                            >
                                Configure Export
                            </Button>
                        )}
                    </Box>
            </Box>

            <ModalDialogComponent
                maxWidth="sm"
                setOpen={setShowConfigModal}
                title={`Configure ${title}`}
                open={showConfigModal}
                confirmText="Configure"
                confirmEnabled={isFormValid && !isConfiguring}
                onConfirm={handleConfigure}
            >
                <ScanExportConfigComponent
                    onFormChange={handleFormChange}
                    formData={formData}
                />
            </ModalDialogComponent>
        </Box>
    );
};

ScanExportPanelComponent.defaultProps = {
    isConfiguring: false,
    isDisabling:
        false,
    currentConfig:
        null,
};

export default ScanExportPanelComponent;
